import styled from '@emotion/styled';
import { useResource } from '@landsbankinn/l16n';
import { device, Segment, typography } from '@landsbankinn/ui';
import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { animated, useSpring } from 'react-spring';

interface NumberItem {
  title: React.ReactNode;
  value: number;
  suffix: string;
  percentage: number;
  chart: boolean;
  type: 'text' | 'chart' | 'bubble';
}

interface AnnualModuleProps {
  copy: React.ReactNode;
  action?: React.ReactNode;
  background?: boolean;
  items?: NumberItem[];
  type: 'text' | 'chart' | 'bubble';
}

const inviewConfig = { delay: 330, threshold: 0.2 };

const Countdown = ({ num, percent, start = false }: any) => {
  const [value, setValue] = useState(0);
  const [stopped, setStopped] = useState(false);
  const { count } = useSpring({
    count: value,
    onRest: () => setStopped(true),
  });
  const { locale } = useResource();

  useEffect(() => {
    if (start) {
      setValue(num);
    }
  }, [num, start]);

  return stopped ? (
    <span>
      {percent ? (num < 0 ? '' : '+') : ''}
      {new Intl.NumberFormat(locale === 'is' ? 'de-DE' : 'en-GB', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      }).format(num)}
      {percent ? '%' : ''}
    </span>
  ) : (
    <animated.span>
      {count.to((x) => {
        return `${percent ? (num < 0 ? '' : '+') : ''}${new Intl.NumberFormat(
          locale === 'is' ? 'de-DE' : 'en-GB',
          {
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
          },
        ).format(x)}${percent ? '%' : ''}`;
      })}
    </animated.span>
  );
};

const Content = styled.div<{ background?: boolean }>`
  background-color: ${(props) =>
    props.background ? '#F4F7F7' : 'transparent'};
  padding: 50px ${(props) => (props.background ? 44 : 0)}px;

  @media ${device.tablet} {
    padding: ${(props) =>
      props.background ? '103px 100px' : '43px 44px 15px'};
    ${(props) => props.background && 'margin: 0 -50px;'};
  }

  @media ${device.ipadPro} {
    padding: ${(props) => (props.background ? '103px 89px' : '43px 89px 15px')};
  }
`;

const Copy = styled.div<{ serif?: boolean }>`
  p {
    ${typography.p2};
    font-family: ${(props) =>
      props.serif ? props.theme.font.familySerif : props.theme.font.family};
  }

  margin-bottom: 90px;
`;

const Div = styled.div<{ type: 'text' | 'chart' | 'bubble' }>``;

const Suffix = styled.span<{ percentage: boolean }>`
  font-size: ${(props) => (props.percentage ? 'inherit' : `18px;`)};
`;
const Title = styled.h5<{ bold?: boolean; color?: string }>`
  ${typography.body}
  color: ${(props) => props.theme.colors.primary};
  font-weight: ${(props) => (props.bold ? '500' : 'normal')};

  ${(props) =>
    props.color
      ? `padding-bottom:20px;margin-bottom:40px;
      border-bottom: 2px solid 
        ${(props.theme.shades as any)[`${props.color || 'primary'}5`]}`
      : ''};
`;

const NumberBox = styled.div`
  border-bottom: 2px solid ${(props) => props.theme.colors.primary};
  margin: 0 30px;
`;

const ChartBox = styled.div<{ color: string }>`
  border-bottom: 2px solid
    ${(props) => (props.theme.shades as any)[`${props.color || 'primary'}5`]};
  margin: 0 40px;

  @media ${device.tablet} {
    margin: 0 20px;
  }

  @media ${device.ipadPro} {
    margin: 0 40px;
  }
`;

const BubbleBox = styled(ChartBox)``;
const Row = styled.div`
  display: flex;
  flex-direction: row;
  height: 80px;
  align-items: center;
  color: ${(props) => props.theme.colors.ternary};
`;

const Value = styled.div`
  ${typography.numbersMedium};
  font-family: ${(props) => props.theme.font.familySlab};
  font-weight: 400;
  flex: 1;
`;

const Percentage = styled.div`
  ${typography.numbersMedium};
  font-family: ${(props) => props.theme.font.familySlab};
  font-weight: 100;
  opacity: 0.5;
`;

const Items = styled.div<{ background?: boolean }>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin: 0 -25px;

  > div {
    width: 100%;
    margin-bottom: 42px;
  }

  @media ${device.tablet} {
    > div {
      width: ${(props) => (props.background ? '33.33%' : '50%')};
    }
  }

  @media ${device.desktop} {
    > div {
      width: 33.33%;
    }
  }
`;

const ChartRow = styled(Row)<{ color: string }>`
  border-top: 2px solid
    ${(props) => (props.theme.shades as any)[`${props.color || 'primary'}5`]};
  border-bottom: 2px solid
    ${(props) => (props.theme.shades as any)[`${props.color || 'primary'}5`]};
  color: ${(props) =>
    (props.theme.shades as any)[`${props.color || 'primary'}5`]};
  margin-top: 12px;
`;

export function NumberItem({ title, value, suffix, percentage }: any) {
  const [ref, inView] = useInView(inviewConfig);
  return (
    <NumberBox ref={ref}>
      <Title>{title}</Title>
      <Row>
        <Value>
          <Countdown num={value} start={inView} />
          <Suffix percentage={suffix == '%'}>{suffix}</Suffix>
        </Value>
        <Percentage>
          {percentage != NaN && (
            <Countdown percent num={percentage * 100} start={inView} />
          )}
        </Percentage>
      </Row>
    </NumberBox>
  );
}

const COLUMN_HEIGHT = 258;

const Columns = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 69px;
  margin-bottom: 44px;
`;

const Column = styled.div`
  text-align: center;
  width: 35%;

  @media ${device.tablet} {
    width: 44%;
  }

  @media ${device.ipadPro} {
    width: 35%;
  }

  ${typography.bodySmall}
`;

const Fill = styled.div<{ color: string; height: number; shade: number }>`
  height: ${(props) => props.height}px;
  width: 100%;
  background: ${(props) =>
    (props.theme.shades as any)[
      `${props.color || 'primary'}${props.shade || 4}`
    ]};
  margin-bottom: 6px;
`;
const Bubble = styled.div<{ color: string }>`
  background: ${(props) =>
    (props.theme.shades as any)[`${props.color || 'primary'}4`]};
  width: 100%;
  padding-top: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 60px;
  line-height: 70px;
  color: white;
  font-family: ${(props) => props.theme.font.familySlab};
  margin-bottom: 40px;
  margin-top: 40px;
  position: Relative;
`;

const BubbleText = styled.div`
  position: absolute;
  top: 50%;
  margin-top: -35px;
`;
export function ChartItem({ title, value, percentage, color }: any) {
  const [ref, inView] = useInView(inviewConfig);
  return (
    <ChartBox color={color} ref={ref}>
      <Title bold>{title}</Title>
      <ChartRow color={color}>
        <Value>
          <Countdown num={value} start={inView} />
        </Value>
        <Percentage>
          {!isNaN(percentage) && (
            <Countdown num={percentage * 100} percent start={inView} />
          )}
        </Percentage>
      </ChartRow>
      <Columns>
        <Column>
          <Fill
            height={
              percentage > 0 ? (1 - percentage) * COLUMN_HEIGHT : COLUMN_HEIGHT
            }
            color={color}
            shade={2}
          />
          2019
        </Column>
        <Column>
          <Fill
            height={
              percentage > 0 ? COLUMN_HEIGHT : (1 + percentage) * COLUMN_HEIGHT
            }
            color={color}
            shade={4}
          />
          2020
        </Column>
      </Columns>
    </ChartBox>
  );
}

export function BubbleItem({ title, value, percentage, color }: any) {
  const [ref, inView] = useInView(inviewConfig);
  return (
    <BubbleBox color={color} ref={ref}>
      <Title bold color={color}>
        {title}
      </Title>
      <Bubble color={color}>
        <BubbleText>{value}</BubbleText>
      </Bubble>
    </BubbleBox>
  );
}

const colors = ['ternary', 'secondary', 'primary'];

const Action = styled.div`
  position: absolute;
  bottom: -30px;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

export function AnnualModule(props: AnnualModuleProps) {
  return (
    <Segment container isArticle>
      <Content background={props.background}>
        <Copy serif={props.background}>{props.copy}</Copy>
        <Items background={props.background}>
          {props.items?.map((item, i) => (
            <Div key={i} type={item.type}>
              {item.type == 'chart' ? (
                <ChartItem color={colors[i % 3]} {...item} />
              ) : item.type === 'bubble' ? (
                <BubbleItem color={colors[i % 3]} {...item} />
              ) : (
                <NumberItem {...item} />
              )}
            </Div>
          ))}
        </Items>
        {props.action && <Action>{props.action}</Action>}
      </Content>
    </Segment>
  );
}
